import { create } from "zustand";

/**
 * Breakout-room state, as the room reports it.
 *
 * This used to be invented here — rooms created locally, participants filled in
 * as the string "Participant", joining a room meaning nothing more than setting
 * an id — and none of it left the tab it happened in. It now mirrors what the
 * signalling server broadcasts, which is the only copy every participant agrees
 * on. `useBreakoutSync` is what fills it; nothing else should write the room
 * list.
 */

export interface BreakoutRoom {
  id: string;
  name: string;
  participants: Array<{
    id: string;
    name: string;
    isHost?: boolean;
    audioOn?: boolean;
    videoOn?: boolean;
  }>;
  isActive: boolean;
}

export interface BreakoutSettings {
  roomCount: number;
  assignmentType: "automatic" | "manual" | "self";
  participantLimit: number;
}

/** How people get into rooms, as it travels on the wire. */
export type BreakoutAssignment = "auto" | "manual" | "self";

type BreakoutRoomsState = {
  breakoutRoomsOpen: boolean;
  breakoutRoomsAssignmentOpen: boolean;
  breakoutSettings: BreakoutSettings | null;
  activeBreakoutRooms: BreakoutRoom[];
  /** The room THIS client is in, or null for the main room. */
  currentBreakoutRoomId: string | null;
  breakoutRoomsPanelOpen: boolean;

  /** True once the room says breakouts are open, whoever opened them. */
  breakoutActive: boolean;
  assignment: BreakoutAssignment;
  /** Where the room says this participant belongs — not where they are yet. */
  myAssignedRoomId: string | null;
  /**
   * The "breakout rooms have started" prompt.
   *
   * Dismissing it must not lose the invitation, which is why it is state rather
   * than a fire-and-forget toast: the control bar offers a way back into it,
   * for the host who cancelled as much as for anyone else.
   */
  invitePromptOpen: boolean;
  /** A move is in flight — the room is being swapped underneath us. */
  switching: boolean;

  setBreakoutRoomsOpen: (v: boolean) => void;
  setBreakoutRoomsAssignmentOpen: (v: boolean) => void;
  setBreakoutSettings: (s: BreakoutSettings | null) => void;
  setActiveBreakoutRooms: (
    rooms: BreakoutRoom[] | ((prev: BreakoutRoom[]) => BreakoutRoom[]),
  ) => void;
  setCurrentBreakoutRoomId: (id: string | null) => void;
  setBreakoutRoomsPanelOpen: (v: boolean) => void;
  setInvitePromptOpen: (v: boolean) => void;
  setSwitching: (v: boolean) => void;

  /** Apply what the room broadcast. */
  /**
   * Everybody in the meeting's main room, as the server sees it.
   *
   * The local roster is only ever the room this client is standing in, so a
   * moderator inside a breakout cannot see the main room — including anyone who
   * joined after they left it. This is how latecomers stay assignable.
   */
  meetingRoster: Array<{ id: string; name: string }>;

  /** How many people fit in one breakout room; null means no limit. */
  roomCapacity: number | null;

  applyBreakoutState: (next: {
    open: boolean;
    assignment?: BreakoutAssignment;
    rooms?: Array<{ id: string; name: string; participantIds: string[] }>;
    /** The main room's roster, so the unassigned pool is the whole meeting. */
    roster?: Array<{ id: string; name: string }>;
    /** Per-room limit, as the room announced it. */
    capacity?: number | null;
    /** Resolves ids to names; the wire carries ids only. */
    nameOf?: (id: string) => string;
    /** This client, so its own assignment can be picked out. */
    selfId?: string | null;
  }) => void;

  /** Rooms are over — back to the main room, prompt gone. */
  clearBreakout: () => void;

  handleCreateBreakoutRooms: (settings: BreakoutSettings) => void;
};

const useBreakoutRoomsStore = create<BreakoutRoomsState>((set, get) => ({
  breakoutRoomsOpen: false,
  breakoutRoomsAssignmentOpen: false,
  breakoutSettings: null,
  activeBreakoutRooms: [],
  currentBreakoutRoomId: null,
  breakoutRoomsPanelOpen: false,
  breakoutActive: false,
  assignment: "auto",
  myAssignedRoomId: null,
  meetingRoster: [],
  roomCapacity: null,
  invitePromptOpen: false,
  switching: false,

  setBreakoutRoomsOpen: (breakoutRoomsOpen) => set({ breakoutRoomsOpen }),
  setBreakoutRoomsAssignmentOpen: (breakoutRoomsAssignmentOpen) =>
    set({ breakoutRoomsAssignmentOpen }),
  setBreakoutSettings: (breakoutSettings) => set({ breakoutSettings }),
  setActiveBreakoutRooms: (rooms) =>
    set({
      activeBreakoutRooms:
        typeof rooms === "function" ? rooms(get().activeBreakoutRooms) : rooms,
    }),
  setCurrentBreakoutRoomId: (currentBreakoutRoomId) => set({ currentBreakoutRoomId }),
  setBreakoutRoomsPanelOpen: (breakoutRoomsPanelOpen) => set({ breakoutRoomsPanelOpen }),
  setInvitePromptOpen: (invitePromptOpen) => set({ invitePromptOpen }),
  setSwitching: (switching) => set({ switching }),

  applyBreakoutState: ({ open, assignment, rooms, roster, capacity, nameOf, selfId }) => {
    if (!open) {
      get().clearBreakout();
      return;
    }

    const list: BreakoutRoom[] = (rooms ?? []).map((r) => ({
      id: r.id,
      name: r.name,
      participants: r.participantIds.map((id) => ({
        id,
        // Real names, resolved from the roster. The wire carries ids because
        // names change and the roster already knows them.
        name: nameOf?.(id) ?? id,
      })),
      isActive: true,
    }));

    const mine = selfId
      ? (rooms ?? []).find((r) => r.participantIds.includes(selfId))?.id ?? null
      : null;

    const wasActive = get().breakoutActive;
    const prevMine = get().myAssignedRoomId;
    const selfWalkIn = (assignment ?? get().assignment) === "self";

    set({
      breakoutActive: true,
      assignment: assignment ?? get().assignment,
      activeBreakoutRooms: list,
      myAssignedRoomId: mine,
      meetingRoster: roster ?? get().meetingRoster,
      roomCapacity: capacity === undefined ? get().roomCapacity : capacity,
      // Prompt only somebody who has somewhere to go.
      //
      // It used to open for anyone present when the rooms opened, which meant a
      // latecomer walking into a meeting with breakouts running was asked about
      // rooms none of which was theirs — news, but not an invitation, and
      // nothing they could act on. The question is only worth asking of a
      // person with an assignment they are not already sitting in.
      //
      // Being moved to a different room is a new invitation, not the same one.
      // The test is against where they actually are, not merely whether the
      // assignment changed: somebody already in the room they were just
      // assigned to has nothing to be asked.
      //
      // Being moved OUT of every room is not a prompt at all — they are simply
      // returned to the main room, the way Leave Room returns them. See
      // useBreakoutSync.
      // Never while a move is in flight. Joining tells the room first and then
      // waits for credentials, and the assignment echo comes back inside that
      // wait — with the destination recorded but this client not yet in it,
      // which reads exactly like "assigned somewhere you have not gone". So a
      // second prompt opened on top of the one just answered.
      invitePromptOpen:
        get().switching
          ? get().invitePromptOpen
          : get().invitePromptOpen ||
        (!!mine && mine !== get().currentBreakoutRoomId && (!wasActive || mine !== prevMine)) ||
        // Self walk-in: nobody is placed, so an assignment is the wrong thing
        // to wait for. Everyone still in the main room is shown the rooms and
        // picks one — without this the mode had no way in at all.
        (selfWalkIn && !mine && !get().currentBreakoutRoomId && !wasActive),
    });
  },

  clearBreakout: () =>
    set({
      breakoutActive: false,
      activeBreakoutRooms: [],
      meetingRoster: [],
      roomCapacity: null,
      // Back to the default, so a mode chosen for rooms that are over does not
      // colour the next set before the room has said anything about it.
      assignment: "auto",
      currentBreakoutRoomId: null,
      myAssignedRoomId: null,
      invitePromptOpen: false,
      breakoutRoomsPanelOpen: false,
      breakoutSettings: null,
      switching: false,
    }),

  // Local only, and deliberately: this is the moderator laying rooms out before
  // anything is announced. Nothing reaches the room until they start them.
  handleCreateBreakoutRooms: (settings) => {
    const rooms: BreakoutRoom[] = Array.from({ length: settings.roomCount }, (_, i) => ({
      id: `room-${i + 1}`,
      name: `Room ${i + 1}`,
      participants: [],
      isActive: false,
    }));
    set({
      breakoutSettings: settings,
      activeBreakoutRooms: rooms,
      breakoutRoomsAssignmentOpen: true,
    });
  },
}));

export function useBreakoutRooms() {
  return useBreakoutRoomsStore();
}

/** For callers outside React (event handlers, the sync hook). */
export const breakoutStore = useBreakoutRoomsStore;
